import { API_URL } from '@/config';
import { formatDate } from '@/utils/dateFormat';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, MapPin, Globe, Lock, CreditCard, FileText, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';
import { getImageUrl } from '@/utils/imageUrl';
import EventRegistrationForm from '@/components/EventRegistrationForm';

const API = `${API_URL}/api`;

const PublicEventPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [form, setForm] = useState(null);
  const [bankDetails, setBankDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRegistration, setShowRegistration] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);

  useEffect(() => {
    loadEventData();
  }, [eventId]);

  const loadEventData = async () => {
    try {
      // Load event from public endpoint
      const eventsRes = await axios.get(`${API}/events/public`);
      const eventData = eventsRes.data.find(e => e.event_id === eventId);
      
      if (!eventData) {
        toast.error('Event not found');
        navigate('/');
        return;
      }
      
      setEvent(eventData);

      // Load registration form if exists
      if (eventData.registration_form_id) {
        try {
          const formRes = await axios.get(`${API}/forms/public/${eventData.registration_form_id}`);
          setForm(formRes.data);
        } catch (err) {
          console.log('No form found');
        }
      }

      // Load bank details for paid events
      if (eventData.payment_type === 'paid') {
        try {
          const bankRes = await axios.get(`${API}/payments/bank-details/public`);
          setBankDetails(bankRes.data);
        } catch (err) {
          console.log('No bank details');
        }
      }
    } catch (error) {
      toast.error('Failed to load event');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const handleRegistrationSuccess = () => {
    setShowRegistration(false);
    setRegistrationSuccess(true);
    toast.success('Registration submitted successfully!');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto mb-4" style={{ color: '#D4AF37' }} />
          <p className="text-white">Loading event...</p>
        </div>
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
        <Card className="max-w-md mx-4">
          <CardContent className="p-8 text-center">
            <h2 className="text-xl font-bold mb-2">Event Not Found</h2>
            <p className="text-muted-foreground mb-4">This event may have been removed or the link is invalid.</p>
            <Button onClick={() => navigate('/')}>Go to Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #0F172A 0%, #1E293B 100%)' }}>
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm border-b border-white/20">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-white hover:text-yellow-400 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Home</span>
          </button>
          <div className="flex items-center gap-2">
            <img src="/lions-favicon.png" alt="Lions Club" className="w-8 h-8" />
            <span className="text-white font-semibold hidden sm:inline">Lions Club Jaipur Hawamahal</span>
          </div>
        </div>
      </header>

      {/* Event Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Card className="overflow-hidden">
          {/* Banner Image */}
          {event.banner_image_url && (
            <div className="h-64 sm:h-80 overflow-hidden bg-gray-100">
              <img 
                src={getImageUrl(event.banner_image_url)} 
                alt={event.title}
                className="w-full h-full object-cover"
                onError={(e) => { e.target.parentElement.style.display = 'none'; }}
              />
            </div>
          )}

          <CardContent className="p-6 sm:p-8">
            {/* Title and Badges */}
            <h1 className="text-2xl sm:text-3xl font-bold mb-4" style={{ color: '#0F172A' }}>{event.title}</h1>
            
            <div className="flex flex-wrap gap-2 mb-6">
              {event.visibility && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-1 ${event.visibility === 'public' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                  {event.visibility === 'public' ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  {event.visibility === 'public' ? 'Public Event' : 'Members Only'}
                </span>
              )}
              {event.payment_type && event.payment_type !== 'free' && (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-amber-100 text-amber-700 inline-flex items-center gap-1">
                  <CreditCard className="w-4 h-4" />
                  {event.registration_amount ? `₹${event.registration_amount}` : 'Paid Event'}
                </span>
              )}
              {event.registration_form_id && (
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700 inline-flex items-center gap-1">
                  <FileText className="w-4 h-4" />
                  Registration Open
                </span>
              )}
            </div>

            {/* Event Details Box */}
            <div className="bg-slate-50 rounded-lg p-6 mb-6">
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Event Date & Time</p>
                  <p className="font-medium flex items-center gap-2">
                    <Calendar className="w-5 h-5" style={{ color: '#D4AF37' }} />
                    {event.event_date ? formatDate(event.event_date) : (event.date_time_start ? formatDate(event.date_time_start) : 'Date to be announced')}
                  </p>
                  {(event.event_time || event.date_time_start) && (
                    <p className="text-sm text-muted-foreground ml-7">
                      at {event.event_time || new Date(event.date_time_start).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  )}
                </div>
                
                {event.venue && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Venue</p>
                    <p className="font-medium flex items-center gap-2">
                      <MapPin className="w-5 h-5" style={{ color: '#D4AF37' }} />
                      {event.venue}
                    </p>
                  </div>
                )}
              </div>

              {event.registration_form_id && (event.registration_start_date || event.registration_end_date) && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs text-muted-foreground uppercase font-medium mb-1">Registration Period</p>
                  <p className="font-medium text-blue-600">
                    {event.registration_start_date 
                      ? formatDate(event.registration_start_date)
                      : 'Now'
                    }
                    {' - '}
                    {event.registration_end_date 
                      ? formatDate(event.registration_end_date)
                      : 'Until Event'
                    }
                  </p>
                </div>
              )}
            </div>

            {/* Description */}
            {event.description && (
              <div className="mb-6">
                <h3 className="font-semibold mb-2">About This Event</h3>
                <p className="text-muted-foreground whitespace-pre-wrap">{event.description}</p>
              </div>
            )}

            {/* Event Images Gallery */}
            {event.event_images && event.event_images.length > 0 && (
              <div className="mb-6">
                <h3 className="font-semibold mb-3">Event Gallery</h3>
                <div className="grid grid-cols-3 gap-2">
                  {event.event_images.map((img, idx) => (
                    <img 
                      key={idx} 
                      src={getImageUrl(img)} 
                      alt={`Event ${idx + 1}`}
                      className="w-full h-24 sm:h-32 object-cover rounded-lg"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Registration Success Message */}
            {registrationSuccess && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-green-800 mb-1">Registration Submitted!</h3>
                <p className="text-green-700 text-sm">
                  Thank you for registering. You will receive a confirmation email shortly.
                  {event.payment_type === 'paid' && ' Your payment is being reviewed.'}
                </p>
              </div>
            )}

            {/* Registration Button */}
            {event.registration_form_id && form && !registrationSuccess && (
              <Button
                onClick={() => setShowRegistration(true)}
                className="w-full text-lg py-6"
                style={{ backgroundColor: '#D4AF37', color: '#0F172A' }}
                data-testid="register-btn"
              >
                <FileText className="w-5 h-5 mr-2" />
                {event.cta_text || 'Register for this Event'}
                {event.registration_amount && ` - ₹${event.registration_amount}`}
              </Button>
            )}

            {event.visibility === 'members_only' && (
              <p className="text-center text-sm text-muted-foreground mt-4">
                This event is for members only. Please login to register.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Share Link Info */}
        <div className="mt-6 text-center text-white/60 text-sm">
          <p>Share this link: <span className="text-yellow-400">{window.location.href}</span></p>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-black/20 py-4 mt-8">
        <div className="max-w-4xl mx-auto px-4 text-center text-white/60 text-sm">
          © 2026 All rights reserved with Cupid Compusoft Pvt. Ltd.
        </div>
      </footer>

      {/* Registration Modal */}
      <Dialog open={showRegistration} onOpenChange={setShowRegistration}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" style={{ color: '#D4AF37' }} />
              Register for {event.title}
            </DialogTitle>
          </DialogHeader>
          
          {form && (
            <EventRegistrationForm
              event={event}
              form={form}
              bankDetails={bankDetails}
              isAuthenticated={false}
              onSuccess={handleRegistrationSuccess}
              onClose={() => setShowRegistration(false)}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PublicEventPage;
